import React from "react";
import { Check, Fish } from "lucide-react";
import SeoLandingLayout from "@/components/SeoLandingLayout";
import { LuxeOverline } from "@/components/LuxeOverline";
import { IMAGES } from "@/lib/site";

const FAQS = [
  {
    q: "Do you offer day tickets for fishing?",
    a: "No — fishing at Walnut Lakes is exclusively for staying guests. We don't sell day tickets or host matches, so the lakes stay quiet and the fish rested all season.",
  },
  {
    q: "What fish are in the lakes?",
    a: "Walnut and Daisy lakes are heavily stocked with mirror and common carp, along with bream and rudd. Eight to nine-pound carp are regulars, and heavier fish show up for the patient rod.",
  },
  {
    q: "How much does fishing cost?",
    a: "Lodge guests fish free with one complimentary rod per person per stay (extra rods £6 each per day). Camping guests pay £6 per day for one rod or £10 per day for two.",
  },
  {
    q: "Do I need a rod licence?",
    a: "Yes — every angler aged 13 or over must hold a valid Environment Agency rod fishing licence and be able to show it on request before casting a line. Licences are available from gov.uk.",
  },
  {
    q: "What are the fishery rules?",
    a: "Barbless hooks only (no micro-barbs), no boilies larger than 15mm, no nut baits or bloodworm, and unhooking mats and landing nets are required for every angler.",
  },
  {
    q: "Can I fish right from my accommodation?",
    a: "Almost — all three lodges have private decks a stone's throw from the water, and many of our lake-view pitches are close enough to keep an eye on the rods.",
  },
];

const RULES = [
  "Barbless hooks only — no micro-barbs",
  "No boilies larger than 15mm",
  "No nut baits or bloodworm",
  "Unhooking mats & landing nets required",
  "Residents only — no day fishing, no matches",
  "Valid Environment Agency rod licence required (age 13+)",
];

const FishingLincolnshire = () => (
  <SeoLandingLayout
    seo={{
      title: "Carp Fishing Holidays in Lincolnshire · Two Private Lakes near Boston",
      description:
        "Fishing holidays in Lincolnshire with two private, guests-only carp lakes. Stay in a lakeside lodge or on a touring pitch and fish Walnut & Daisy lakes — heavily stocked, adults only.",
      path: "/fishing-holidays-lincolnshire",
    }}
    overline="Fishing holidays in Lincolnshire"
    titlePre="Carp fishing holidays on two private"
    titleEm="Lincolnshire lakes."
    intro="Walnut Lakes is an adults-only holiday park built around two private, heavily stocked carp lakes between Boston and Spalding. Fishing is reserved for staying guests only — no day tickets, no matches — so the water stays quiet from opening day to the end of the season."
    heroImage={IMAGES.fishingLake}
    heroAlt="Dawn over the private carp fishing lake at Walnut Lakes, Lincolnshire"
    faqs={FAQS}
  >
    {/* The lakes */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <LuxeOverline>Walnut &amp; Daisy</LuxeOverline>
        <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
          Two lakes. Plenty of carp.
        </h2>
        <p className="mt-6 text-sm md:text-base leading-relaxed text-[#D8CDBA]">
          Our two private waters are heavily stocked with mirror and common carp, alongside
          bream and rudd. Eight to nine-pounders are regulars here, and heavier fish make an
          appearance for the patient rod. Because only staying guests can fish, there's never
          a queue for a swim.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-6">
          <div className="rounded-2xl bg-[#14100F] border border-white/10 p-6">
            <div className="font-serif text-3xl text-[#F5EFE6]">Free</div>
            <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#E4B2A8]">For lodge guests</div>
            <p className="mt-3 text-sm text-[#D8CDBA]">One complimentary rod per person per stay.</p>
          </div>
          <div className="rounded-2xl bg-[#14100F] border border-white/10 p-6">
            <div className="font-serif text-3xl text-[#F5EFE6]">£6/day</div>
            <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#E4B2A8]">Camping guests</div>
            <p className="mt-3 text-sm text-[#D8CDBA]">One rod — or two rods for £10/day.</p>
          </div>
        </div>
      </div>
      <img
        src={IMAGES.niceFish}
        alt="A fine common carp caught at Walnut Lakes private fishery, Lincolnshire"
        className="w-full h-[380px] object-cover rounded-2xl"
        loading="lazy"
      />
    </section>

    {/* Rules */}
    <section className="bg-[#14100F] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <LuxeOverline>Rules of the water</LuxeOverline>
        <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
          Fish care comes first
        </h2>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-3">
          {RULES.map((r) => (
            <li key={r} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
              <Fish size={15} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {r}
            </li>
          ))}
        </ul>
        <p className="mt-8 text-sm text-[#D8CDBA] max-w-2xl">
          Every angler aged 13 or over must hold a valid Environment Agency rod fishing licence
          and produce it on request — no licence, no fishing, no exceptions.{" "}
          <a
            href="https://www.gov.uk/fishing-licences/buy-a-fishing-licence"
            target="_blank"
            rel="noopener"
            className="link-underline text-[#F5EFE6]"
            data-testid="seo-fishing-licence-link"
          >
            Buy a licence at gov.uk
          </a>
          .
        </p>
      </div>
    </section>

    {/* Stay & fish */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Stay &amp; fish</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4 max-w-2xl">
        Wake up on the water's edge
      </h2>
      <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-[#D8CDBA]">
        Choose from three lakeside log lodges with private decks, or lake-view hardstanding and
        grass pitches for tourers and tents. Our season runs mid-March to the end of November,
        and the park sits just off the A16/A17 near Sutterton — easy to reach from anywhere in
        Lincolnshire and beyond.
      </p>
      <ul className="mt-8 space-y-3">
        {[
          "Lakeside lodges from £90/night — fishing included",
          "Touring & tent pitches from £24/night",
          "Adults only (15+) — quiet water, guaranteed",
          "Dog friendly, with a half-mile enclosed dog walk",
        ].map((i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
            <Check size={16} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {i}
          </li>
        ))}
      </ul>
    </section>
  </SeoLandingLayout>
);

export default FishingLincolnshire;
